<template>
  <div
    class="flex flex-col"
    :data-stats-frozen="isFrozen ? '1' : '0'"
    :data-stats-loading="loading ? '1' : '0'"
    :data-stats-loaded="isLoaded ? '1' : '0'"
    data-stats-view
  >
    <UpgradeBanner
      v-if="user && limited && !user.isFrozen && !hasComplaints"
      data-id="stats-users-limited"
      show
      flat
      description="You have exceeded your plan limits. Please upgrade."
      text="Upgrade your plan to increase usage limits and unlock more features."
    />
    <Spinner v-if="loading" />
    <CardInside v-else>
      <template #body>
        <div class="grid grid-cols-1 gap-8 lg:grid-cols-4">
          <Fraction
            v-for="item in items"
            :id="item.id"
            :key="item.dataId"
            :fraction-type="item.fractionType"
            :disabled="item.disabled"
            :title="item.title"
            :alert="item.alert"
            :data-id="item.dataId"
            :link="item.link"
            :thresholds="item.thresholds"
            :show-limit-danger="item.showLimitDanger"
            @limited="onLimited"
            @loaded="onLoaded"
          />
        </div>
      </template>
      <template #help>
        <p>
          Upgrade
          <NuxtLink class="text-link" to="/upgrade/"
            >your subscription</NuxtLink
          >
          to increase limits.
        </p>
        <p>
          If you are blocked please
          <a class="text-link" :href="links.support.url" target="_blank"
            >contact support</a
          >.
        </p>
      </template>
    </CardInside>
  </div>
</template>
<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import Fraction from '~/components/charts/Fraction.vue';
import Spinner from '~/components/Spinner.vue';
import { GetStatisticsFractionFractionTypeEnum } from '@mailslurp/admin-sdk';

import { useStore3 } from '~/store/main';
import { storeToRefs } from 'pinia';
import { loadFetch } from '~/lib/services/helpers';
import UpgradeBanner from '~/components/UpgradeBanner.vue';
import CardInside from '~/components/form/CardInside.vue';
import { links } from '@mailslurp/common/dist/links';
import { getLogger } from '~/lib/services/getLogger';

const items = computed<
  {
    fractionType: GetStatisticsFractionFractionTypeEnum;
    title: string;
    alert: string;
    id: string;
    dataId: string;
    link: string;
    showLimitDanger?: boolean;
    disabled?: string;
    thresholds?: {
      danger: number;
      warning: number;
    };
  }[]
>(() => {
  return [
    {
      fractionType: GetStatisticsFractionFractionTypeEnum.MONTHLY_INBOXES,
      alert:
        'You have reached or exceeded your monthly inbox limit. Please upgrade your account to create more inboxes this month.',
      title: 'Monthly new inboxes',
      id: 'inboxes',
      dataId: 'inboxes',
      showLimitDanger: true,
      link: '/inboxes/create/',
    },
    {
      fractionType: GetStatisticsFractionFractionTypeEnum.MONTHLY_SEND,
      title: 'Monthly sends',
      alert:
        'You have reached or exceeded your monthly send limit. Please upgrade your account to send more emails this month.',
      id: 'send',
      dataId: 'send',
      link: '/compose/',
      showLimitDanger: true,
    },
    {
      fractionType: GetStatisticsFractionFractionTypeEnum.MONTHLY_RECEIVE,
      title: 'Monthly receives',
      alert:
        'You have reached or exceeded your monthly receive limit. Please upgrade your account to receive more emails this month.',
      id: 'receive',
      dataId: 'receive',
      link: '/emails/',
      showLimitDanger: true,
    },
    {
      fractionType:
        GetStatisticsFractionFractionTypeEnum.CURRENT_MAX_TOTAL_COMPLAINTS,
      alert:
        'You have exceeded your total complaint limit. Please contact support.',
      id: 'complaints',
      dataId: 'complaints',
      link: '/reputation/',
      showLimitDanger: true,
      title: 'Complaints',
    },
    {
      fractionType: GetStatisticsFractionFractionTypeEnum.CURRENT_DOMAINS,
      alert:
        'You have exceeded your total domain limit. Please upgrade your account to create more domains.',
      id: 'domains',
      dataId: 'domains',
      link: '/domains/',
      disabled: 'Upgrade your account to use custom domains',
      title: 'Custom domains',
    },
    {
      fractionType: GetStatisticsFractionFractionTypeEnum.CURRENT_MEMBERS,
      title: 'Team members',
      alert:
        'You have exceeded your total team size. Please upgrade your account to add more team members.',
      id: 'members',
      dataId: 'members',
      link: '/organizations/',
      disabled: 'Upgrade your account to add team members',
    },
    {
      fractionType:
        GetStatisticsFractionFractionTypeEnum.CURRENT_MAX_DAILY_BOUNCES,
      title: 'Daily bounces',
      alert:
        'You have exceeded the max daily email bounces for your account at the current plan. Please contact support to unlock.',
      id: 'maxDaily',
      dataId: 'maxDaily',
      link: '/reputation/bounces/',
      showLimitDanger: true,
      thresholds: {
        warning: 0,
        danger: 50,
      },
    },
    {
      fractionType:
        GetStatisticsFractionFractionTypeEnum.CURRENT_MAX_TOTAL_BOUNCES,
      title: 'Max bounces',
      alert:
        'You have exceeded the total email bounces for your account at the current plan. Please contact support to unlock.',
      id: 'maxBounces',
      dataId: 'maxBounces',
      link: '/reputation/bounces/',
      showLimitDanger: true,
      thresholds: {
        warning: 0,
        danger: 50,
      },
    },
  ];
});

const store3 = useStore3();

const { user, complaintPagination } = storeToRefs(store3);
const loadedCount = ref(0);
const onLoaded = () => loadedCount.value++;
const isLoaded = computed(() => loadedCount.value === items.value.length);
const log = getLogger('Stats');
const loading = ref(false);
const hasComplaints = computed(
  () => (complaintPagination.value?.numberOfElements ?? 0) > 0,
);
const limited = ref(false);

const emit = defineEmits<{ (e: 'limited'): void }>();

const onLimited = () => {
  limited.value = true;
  log('Stats is limited');
  emit('limited');
};

onMounted(async () => {
  log('Stats mounted');
  if (user.value && user.value.isFrozen) {
    emit('limited');
  }
  await loadFetch(
    () =>
      store3.getComplaintEmailPagination({
        size: 1,
      }),
    store3,
    loading,
  );
});

const isFrozen = computed(() => {
  return user.value?.isFrozen;
});
</script>
